/**
 * Release 3 REV-2 — side panel with the board subtree's recent comments and activity (what the caller can VIEW),
 * newest first, with a comments / activity / all filter. Items deep-link to the asset (overlay) or board.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Activity, X } from 'lucide-react';
import { useBoardActivity, type BoardActivityItem } from '@/lib/api/queries';
import type { BoardActivityScopeParam } from '@/lib/api/keys';
import { Button, IconButton } from '@/components/Button';
import { Avatar, EmptyState, Spinner, Tabs } from '@/components/Misc';
import { formatRelative } from '@/lib/format';
import { activityLine } from './activityText';

export function ActivityPanel({ boardId, ws, onClose }: { boardId: string; ws: string; onClose: () => void }) {
  const [scope, setScope] = useState<BoardActivityScopeParam>('all');
  const q = useBoardActivity(boardId, scope);
  const navigate = useNavigate();
  const location = useLocation();
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const open = (i: BoardActivityItem) => {
    if (i.asset_id) navigate(`/w/${ws}/a/${i.asset_id}`, { state: { background: location.pathname + location.search } });
    else if (i.board_id !== boardId) navigate(`/w/${ws}/b/${i.board_id}`);
  };
  return (
    <aside role="complementary" aria-label="Board activity" data-activity-panel className="flex h-full w-80 shrink-0 flex-col border-l border-line bg-bg-elev">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2">
        <Activity size={14} className="text-fg-subtle" />
        <h2 className="text-sm font-semibold">Activity</h2>
        <IconButton label="Close activity" size="sm" className="ml-auto" onClick={onClose}><X size={14} /></IconButton>
      </div>
      <Tabs value={scope} onChange={setScope} label="Activity filter" className="px-2" tabs={[{ id: 'all', label: 'All' }, { id: 'comments', label: 'Comments' }, { id: 'activity', label: 'Changes' }]} />
      <ul className="min-h-0 flex-1 overflow-y-auto" aria-label="Activity items">
        {q.isPending && <li className="flex justify-center py-8 text-fg-muted"><Spinner /></li>}
        {!q.isPending && items.length === 0 && <li><EmptyState title="No activity yet" body="Comments, uploads and approvals in this board and its sub-boards show up here." /></li>}
        {items.map((i) => (
          <li key={i.id} data-activity-item={i.kind} data-activity-action={i.action} className="border-b border-line last:border-b-0">
            <button type="button" className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-bg-sunken" onClick={() => open(i)}>
              <Avatar actor={i.actor} size={24} className="mt-0.5 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm leading-snug">{activityLine(i)}</span>
                {i.kind === 'comment' && i.comment?.body && <span className="mt-0.5 block truncate text-xs text-fg-muted">“{i.comment.body}”</span>}
                <span className="mt-0.5 block text-[11px] text-fg-subtle">{formatRelative(i.created_at)}</span>
              </span>
            </button>
          </li>
        ))}
        {q.hasNextPage && (
          <li className="p-2 text-center">
            <Button size="sm" variant="ghost" onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>{q.isFetchingNextPage ? 'Loading…' : 'Load more'}</Button>
          </li>
        )}
      </ul>
    </aside>
  );
}
